import { gql } from "@apollo/client";

const careEventDiagnosisFragment = gql`
  fragment CareEventDiagnosis on VisitCareEvent {
    diagnosisList {
      type
      uuid
      notes
      diseaseSnapshot
    }
  }
`;

const inpatientLocationFragment = gql`
  fragment InpatientLocation on VisitLocationSnapshot {
    _id
    name
    created
    color
    performer
  }
`;

export const GET_VISIT_BY_ID_QUERY = gql`
  query VisitById($id: ID!) {
    visitById(id: $id) {
      _id
      isCurrent
      currentLocation {
        _id
        name
      }
      currentDepartment {
        _id
        title
      }
      admissionEvent {
        _id
        type
        created
        notes
        performer {
          name
        }
        ...CareEventDiagnosis
      }
      dischargeEvent {
        _id
        type
        created
        outcome
        notes
        performer {
          name
        }
        ...CareEventDiagnosis
      }
      inpatientLocations {
        ...InpatientLocation
      }
      inpatientDepartments {
        _id
        title
        careEventCreatedAt
        performer
      }
    }
  }
  ${inpatientLocationFragment}
  ${careEventDiagnosisFragment}
`;

export const GET_VISIT_NOTES_QUERY = gql`
  query getVisitNotes($input: VisitWithFilteredNotesInput!) {
    visitWithFilteredNotes(input: $input) {
      _id
      careEvents {
        _id
        created
        type
        notes
        performer {
          _id
          name
          role
        }
      }
    }
  }
`;

export const UPDATE_INPATIENT_LOCATION = gql`
  mutation updateInpatientLocation($input: UpdateInpatientLocationInput!) {
    updateInpatientLocation(input: $input) {
      _id
      inpatientLocations {
        ...InpatientLocation
      }
    }
  }
  ${inpatientLocationFragment}
`;

export const UPDATE_INPATIENT_DEPARTMENT = gql`
  mutation updateInpatientDepartment($input: UpdateInpatientDepartmentInput!) {
    updateInpatientDepartment(input: $input) {
      _id
      inpatientDepartments {
        _id
        title
        careEventCreatedAt
      }
    }
  }
`;

export const ADD_NOTE_MUTATION = gql`
  mutation addNote($input: AddNoteToVisitInput!) {
    addNoteToCurrentVisit(input: $input) {
      _id
      careEvents {
        _id
        created
        type
        notes
        performer {
          _id
          name
          role
        }
      }
    }
  }
`;

const investigationCareEventFragment = gql`
  fragment InvestigationCareEvent on VisitCareEvent {
    _id
    type
    created
    investigationActivity {
      _id
      void
      meta {
        created
        updated
      }
      catalogueItem {
        _id
        title
        activityType
        coding {
          loinc {
            code
          }
        }
        keywords
        result {
          resultSchema
        }
        meta {
          created
          updated
        }
      }
      request {
        performer {
          _id
          name
        }
        organisation {
          uuid
          name
        }
        clinic {
          _id
          uuid
          name
        }
        orderedProperties
      }
      outcome {
        performer {
          _id
          name
        }
        organisation {
          uuid
          name
        }
        clinic {
          _id
          uuid
          name
        }
        orderedProperties
      }
    }
    performer {
      _id
      name
    }
  }
`;

export const GET_VISIT_INVESTIGATIONS_QUERY = gql`
  query getVisitInvestigations($id: ID!) {
    visitById(id: $id) {
      _id
      isCurrent
      careEvents(
        input: { types: [INVESTIGATION_REQUEST, INVESTIGATION_OUTCOME, INVESTIGATION_RESULTS] }
      ) {
        ...InvestigationCareEvent
      }
    }
  }
  ${investigationCareEventFragment}
`;

gql`
  query PrintInpatientSummary($uuid: String!, $searchWholeTerritory: Boolean) {
    patientByCardId(uuid: $uuid, searchWholeTerritory: $searchWholeTerritory) {
      passportId
      patient {
        uuid
        card {
          id
        }
        person {
          name {
            firstname
            middlename
            lastname
          }
          dob
          gender
        }
      }
    }
  }
`;

gql`
  query getInpatientInvestigationsMedicines($visitId: ID!) {
    visitById(id: $visitId) {
      _id
      investigations: careEvents(input: { types: [INVESTIGATION_OUTCOME, INVESTIGATION_RESULTS] }) {
        _id
        type
        investigationActivity {
          _id
          meta {
            created
          }
          catalogueItem {
            _id
            title
            meta {
              created
            }
          }
          request {
            performer {
              _id
              name
            }
            orderedProperties
          }
          outcome {
            performer {
              _id
              name
            }
            orderedProperties
          }
        }
        performer {
          _id
          name
        }
      }
      inpatientMedicineAdministered {
        skuId
        skuName
        created
        prescriber
        dosageValue
        dosageUnit
        totalAdministeredQuantity
      }
    }
  }
`;

export const inpatientGraphql = {
  fragments: {
    careEventDiagnosisFragment,
    inpatientLocationFragment,
    investigationCareEventFragment,
  },
};
